// The GENERIC sync-chain diagnostic behind `/debug` — every provider's wiring for one band, plus a per-row
// "test run" that proves the chain by actually fetching.
//
// Raw fetch rather than the generated client, for the same reason as `useSongstatsDebug`: the controller is
// `[InternalController]`, so its routes are deliberately absent from `openapi.json` and therefore from the
// generated schema. That is what keeps a diagnostic surface out of the committed contract snapshot and away
// from CI's drift gates. The cost is that the wire types are hand-mirrored from
// `SyncChainDiagnosticsModels.cs`.
//
// Rows run ONE PER REQUEST rather than all in one call. The point of the page is to find the provider that is
// broken or hanging, and a single batched call would let one hanging provider stall every other row's answer —
// hiding the exact symptom being looked for.
import { useCallback, useState } from "react";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

export type SyncTraceStatus = "ok" | "warn" | "fail" | "skipped" | "pending";

/** One outbound vendor call, as it was actually sent. */
export interface TracedCall {
  method: string;
  /** The URL as sent. Safe to show: the API key rides the `apikey` header, never the query string. */
  url: string;
  status: number | null;
  outcome: string;
  ms: number;
  bytes: number | null;
}

export interface SyncTraceRow {
  provider: string;
  kind: string;
  status: SyncTraceStatus;
  /** The line to read first — what landed, or why nothing was called. */
  summary: string;
  calls: TracedCall[];
  landed: string | null;
  ms: number;
}

/** A step the refresh takes before any vendor fetch. */
export interface SyncPreStep {
  name: string;
  status: SyncTraceStatus;
  detail: string;
}

export interface SyncTraceDoc {
  bandId: string;
  envName: string;
  /** Where these calls actually go. Green against a stand-in and green against production are different
   *  claims, and the page says which rather than letting them read alike. */
  vendorHost: string;
  vendorIsFake: boolean;
  generatedOn: string;
  preSteps: SyncPreStep[];
  /** EVERY registered provider, including ones this band has no schedule for — a missing schedule is one of
   *  the failures this page exists to find, and a list of what exists cannot show what does not. */
  rows: SyncTraceRow[];
  /** Integrations that deliberately sit outside the sync engine, named so their absence is not read as a gap. */
  notSynced: string[];
}

const CHAIN_KEY = ["debug", "sync-chain"] as const;

async function chainFetch<T>(bearer: string | null, url: string, method: "GET" | "POST"): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
  });

  if (!response.ok) {
    // A 404 is the expected shape in Production, where [InternalController] removes the route entirely.
    let problem: unknown;
    try {
      problem = await response.json();
    } catch {
      problem = undefined;
    }
    throw new ApiError(response.status, problem);
  }

  const body = (await response.json()) as { data?: T };
  if (!body?.data) throw new ApiError(response.status, undefined);
  return body.data;
}

/** The whole estate, read-only. No vendor is called. */
export function useSyncChain() {
  const { tokenProvider } = useApp();
  return useBandQuery<SyncTraceDoc>(CHAIN_KEY, (band) =>
    chainFetch<SyncTraceDoc>(
      tokenProvider.getBearer(),
      `${API_URL}/api/v1/bands/${band}/debug/sync`,
      "GET",
    ),
  );
}

/**
 * Run every provider, each on its own request, updating rows as they land.
 *
 * Deliberately not a mutation: react-query would give one pending flag for the batch, and the whole value of
 * the page is watching rows resolve INDEPENDENTLY. A provider that hangs should hang on its own line while
 * its neighbours go green, because "which one is stuck" is the question being asked.
 */
export function useSyncChainRun() {
  const { tokenProvider } = useApp();
  const { bandId } = useBandContext();
  const [results, setResults] = useState<Record<string, SyncTraceRow>>({});
  const [running, setRunning] = useState<Set<string>>(new Set());
  const [preSteps, setPreSteps] = useState<SyncPreStep[]>([]);

  const run = useCallback(
    async (providers: string[]) => {
      if (!bandId) return;

      setRunning(new Set(providers));
      setResults({});
      setPreSteps([]);

      // FIRST, in the same order the real refresh does it: ensure the schedules exist and re-resolve the
      // release links. Fetching before this would diagnose a band whose jobs were never created in the first
      // place — the exact failure being looked for — and conclude the providers were fine.
      try {
        const steps = await chainFetch<SyncPreStep[]>(
          tokenProvider.getBearer(),
          `${API_URL}/api/v1/bands/${bandId}/debug/sync/pre`,
          "POST",
        );
        setPreSteps(steps);
      } catch (error) {
        setPreSteps([
          {
            name: "pre-steps",
            status: "fail",
            detail: `could not run: ${error instanceof ApiError ? `HTTP ${error.status}` : String(error)}`,
          },
        ]);
      }

      await Promise.all(
        providers.map(async (provider) => {
          try {
            const row = await chainFetch<SyncTraceRow>(
              tokenProvider.getBearer(),
              `${API_URL}/api/v1/bands/${bandId}/debug/sync/run/${encodeURIComponent(provider)}`,
              "POST",
            );
            setResults((prior) => ({ ...prior, [provider]: row }));
          } catch (error) {
            // A failed REQUEST is not a failed chain, and conflating them would blame a provider for the
            // page's own network trouble. Rendered as a fail row that says so in its own words.
            const detail =
              error instanceof ApiError
                ? `the diagnostic request itself failed (HTTP ${error.status})`
                : `the diagnostic request itself failed: ${String(error)}`;
            setResults((prior) => ({
              ...prior,
              [provider]: {
                provider,
                kind: "",
                status: "fail",
                summary: detail,
                calls: [],
                landed: null,
                ms: 0,
              },
            }));
          } finally {
            setRunning((prior) => {
              const next = new Set(prior);
              next.delete(provider);
              return next;
            });
          }
        }),
      );
    },
    [bandId, tokenProvider],
  );

  return { run, results, running, preSteps, isRunning: running.size > 0 };
}
